import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { submitReview } from "@/lib/reviews.functions";
import { reportIncident } from "@/lib/incidents.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

type Row = {
  shift_id: string;
  booking_id: string;
  start_time: string;
  end_time: string;
  role_type: string;
  status: string;
  address: string | null;
  postcode: string | null;
  venue_id: string;
  venue_name: string | null;
  reviewed: boolean;
};

export const Route = createFileRoute("/_authenticated/bartender/previous")({
  head: () => ({ meta: [
    { title: "Previous engagements — The Hospitality Reserve" },
    { name: "description", content: "Your completed and cancelled shifts. Rate the venue and flag any post-shift issues." },
  ] }),
  component: BartenderPrevious,
});

function BartenderPrevious() {
  const navigate = useNavigate();
  const reviewFn = useServerFn(submitReview);
  const reportFn = useServerFn(reportIncident);

  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [reviewRow, setReviewRow] = useState<Row | null>(null);
  const [reportRow, setReportRow] = useState<Row | null>(null);

  const load = async () => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user ?? null;
    if (!user) { navigate({ to: "/auth" }); return; }

    const { data, error } = await supabase
      .from("bookings")
      .select(`
        id, shift_id,
        shift:shifts!inner(
          id, start_time, end_time, status, role_type, address, postcode, venue_id
        )
      `)
      .eq("bartender_id", user.id)
      .eq("status", "accepted");
    if (error) { toast.error(error.message); setLoading(false); return; }
    const records = (data ?? []) as unknown as Array<{
      id: string; shift_id: string;
      shift: { id: string; start_time: string; end_time: string; status: string;
        role_type: string; address: string | null; postcode: string | null; venue_id: string };
    }>;
    const past = records.filter((r) => ["completed", "cancelled"].includes(r.shift.status));
    const venueIds = Array.from(new Set(past.map((r) => r.shift.venue_id)));
    const nameMap = new Map<string, string>();
    if (venueIds.length) {
      const { data: profs } = await supabase.from("profiles").select("id, full_name").in("id", venueIds);
      (profs ?? []).forEach((p) => nameMap.set(p.id, (p.full_name as string | null) || "Venue"));
    }
    const shiftIds = past.map((r) => r.shift_id);
    const reviewed = new Set<string>();
    if (shiftIds.length) {
      const { data: revs } = await supabase
        .from("reviews")
        .select("shift_id, direction, bartender_id")
        .in("shift_id", shiftIds)
        .eq("direction", "bartender_to_venue")
        .eq("bartender_id", user.id);
      (revs ?? []).forEach((r) => reviewed.add(r.shift_id as string));
    }

    setRows(past.map((r) => ({
      shift_id: r.shift_id,
      booking_id: r.id,
      start_time: r.shift.start_time,
      end_time: r.shift.end_time,
      role_type: r.shift.role_type,
      status: r.shift.status,
      address: r.shift.address,
      postcode: r.shift.postcode,
      venue_id: r.shift.venue_id,
      venue_name: nameMap.get(r.shift.venue_id) ?? null,
      reviewed: reviewed.has(r.shift_id),
    })).sort((a, b) => new Date(b.end_time).getTime() - new Date(a.end_time).getTime()));
    setLoading(false);
  };

  useEffect(() => { void load(); }, []);

  return (
    <div className="min-h-dvh bg-background">
      <ReserveHeader role="bartender" />
      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Archive</p>
            <h1 className="mt-3 font-display text-3xl">Previous engagements</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Rate the venues you worked with. Flag any post-shift issues within the reporting window.
            </p>
          </div>
          <Link to="/bartender" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">
            Back to feed
          </Link>
        </div>
        <span className="gold-divider mt-4" />

        {loading ? (
          <p className="mt-10 text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="mt-8 border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
            Nothing here yet. Completed shifts will appear on this page.
          </div>
        ) : (
          <ul className="mt-8 space-y-3">
            {rows.map((r) => {
              const start = new Date(r.start_time);
              const end = new Date(r.end_time);
              const canReport = Date.now() < end.getTime() + 6 * 3_600_000;
              return (
                <li key={r.booking_id} className="border border-border bg-card p-5">
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="tracking-wide-luxury text-[0.65rem] text-muted-foreground uppercase">{r.status}</p>
                      <p className="mt-1 font-display text-lg capitalize">
                        {r.role_type.replace(/_/g, " ")} · {start.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        {" – "}
                        {end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                        {r.venue_name ? ` · ${r.venue_name}` : ""}
                      </p>
                      {(r.address || r.postcode) && (
                        <p className="text-xs text-muted-foreground">📍 {[r.address, r.postcode].filter(Boolean).join(" · ")}</p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to="/shift/$id/notes" params={{ id: r.shift_id }}
                        className="border border-accent text-accent px-3 py-2 tracking-wide-luxury text-[0.65rem] hover:bg-accent hover:text-accent-foreground"
                      >
                        Message venue →
                      </Link>
                      {r.status === "completed" && !r.reviewed && (
                        <Button
                          type="button"
                          onClick={() => setReviewRow(r)}
                          className="rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury text-xs"
                        >
                          Rate venue
                        </Button>
                      )}
                      {r.status === "completed" && r.reviewed && (
                        <span className="text-[0.65rem] tracking-wide-luxury text-muted-foreground self-center">✓ Reviewed</span>
                      )}
                      {canReport && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setReportRow(r)}
                          className="rounded-none tracking-wide-luxury text-xs text-destructive"
                        >
                          Report issue
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      <ReviewVenueDialog
        row={reviewRow}
        onClose={() => setReviewRow(null)}
        onSubmitted={() => { setReviewRow(null); void load(); }}
        submit={reviewFn}
      />
      <ReportIssueDialog
        row={reportRow}
        onClose={() => setReportRow(null)}
        onSubmitted={() => setReportRow(null)}
        submit={reportFn}
      />
    </div>
  );
}

function ReviewVenueDialog({
  row, onClose, onSubmitted, submit,
}: {
  row: Row | null;
  onClose: () => void;
  onSubmitted: () => void;
  submit: (args: { data: { shift_id: string; rating: number; comment?: string | null; direction: "bartender_to_venue" } }) => Promise<unknown>;
}) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (row) { setRating(5); setComment(""); } }, [row]);

  const send = async () => {
    if (!row) return;
    setBusy(true);
    try {
      await submit({ data: { shift_id: row.shift_id, rating, comment: comment.trim() || null, direction: "bartender_to_venue" } });
      toast.success("Thanks — review submitted.");
      onSubmitted();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not submit review.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Rate the venue</DialogTitle>
          <DialogDescription>
            Reviews are private to admin moderation and help keep standards high.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Rating</Label>
            <div className="mt-2 flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className={`h-9 w-9 border text-sm ${rating >= n ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground"}`}
                  aria-label={`${n} star${n === 1 ? "" : "s"}`}
                >
                  ★
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Comment (optional)</Label>
            <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} maxLength={1000} className="mt-2 rounded-none" />
          </div>
          <Button onClick={send} disabled={busy} className="w-full rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-6">
            {busy ? "Submitting…" : "Submit review"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ISSUE_TYPES = [
  { value: "safety_concern", label: "Safety concern" },
  { value: "behavioral", label: "Behaviour / conduct" },
  { value: "aggression", label: "Aggression or threats" },
  { value: "other", label: "Other" },
] as const;

function ReportIssueDialog({
  row, onClose, onSubmitted, submit,
}: {
  row: Row | null;
  onClose: () => void;
  onSubmitted: () => void;
  submit: (args: { data: { shift_id: string; type: (typeof ISSUE_TYPES)[number]["value"]; description: string; evidence_urls?: string[] } }) => Promise<unknown>;
}) {
  const [type, setType] = useState<(typeof ISSUE_TYPES)[number]["value"]>("safety_concern");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const canSubmit = useMemo(() => description.trim().length >= 10, [description]);

  useEffect(() => { if (row) { setType("safety_concern"); setDescription(""); } }, [row]);

  const send = async () => {
    if (!row) return;
    setBusy(true);
    try {
      await submit({ data: { shift_id: row.shift_id, type, description: description.trim() } });
      toast.success("Report filed. Our team will follow up.");
      onSubmitted();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not file report.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Report an issue</DialogTitle>
          <DialogDescription>
            Reports go to Reserve admin. False or malicious reports can affect your standing.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Type</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {ISSUE_TYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className={`border px-3 py-2 text-xs tracking-wide-luxury text-left ${type === t.value ? "border-accent bg-accent/10 text-accent" : "border-border text-muted-foreground"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>What happened? (min. 10 characters)</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5} maxLength={4000} className="mt-2 rounded-none" />
          </div>
          <Button onClick={send} disabled={!canSubmit || busy} className="w-full rounded-none bg-destructive text-destructive-foreground hover:opacity-90 tracking-wide-luxury py-6">
            {busy ? "Submitting…" : "Submit report"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
